import {
  CAPTURE_REQUEST_HEADER,
  CaptureDeviceClass,
  type CaptureFailureReason,
  type CaptureSessionView,
  type FaceCandidateInput,
  type MeasurementAttemptOutcome,
} from '@optiflow/types';

/**
 * The capture page's only link to the API.
 *
 * Deliberately not the app's API client: no staff token, no branch, no refresh loop — a phone on
 * this page is not signed in and must never look as if it were. Same origin by design, so the
 * HttpOnly capability cookie scoped to this path travels with every call and nothing else does.
 */
const CAPTURE_API = '/api/v1/capture';

export type CaptureFailure = 'ENDED' | 'UNUSABLE_CODE' | 'REFUSED' | 'NETWORK';

export class CaptureRequestError extends Error {
  constructor(readonly kind: CaptureFailure) {
    super(kind);
  }
}

async function call<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${CAPTURE_API}${path}`, {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      headers: {
        [CAPTURE_REQUEST_HEADER]: '1',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new CaptureRequestError('NETWORK');
  }

  if (response.status === 401) throw new CaptureRequestError('ENDED');
  if (response.status === 404) throw new CaptureRequestError('UNUSABLE_CODE');
  if (!response.ok) throw new CaptureRequestError('REFUSED');

  const payload = (await response.json()) as { data: T };
  return payload.data;
}

/** A finished estimate, in the shape the desk API stores. Millimetres are decimal strings; monocular PD is not measured by this method and is never derived. */
export interface CaptureCandidate {
  submissionId: string;
  totalPdMm: string;
  calibrationMethod: 'IRIS_REFERENCE';
  algorithmVersion: string;
  landmarkQualityBp: number;
  calibrationQualityBp: number;
  poseQualityBp: number;
  stabilityQualityBp: number;
  overallQualityBp: number;
  acceptedFrameCount: number;
  deviceClass: CaptureDeviceClass;
}

export const captureClient = {
  redeem: (token: string, deviceClass: CaptureDeviceClass) =>
    call<CaptureSessionView>('POST', '/redeem', { token, deviceClass }),
  session: () => call<CaptureSessionView>('GET', '/session'),
  /** The result, and nothing else: no frame, no landmark, no per-frame geometry ever crosses this call. */
  candidate: (body: CaptureCandidate) =>
    call<CaptureSessionView>('POST', '/session/candidate', body),
  /** A face suggestion from this phone. The same rule holds: a shape and its reasons, never a picture of anyone. */
  faceCandidate: (body: FaceCandidateInput) =>
    call<CaptureSessionView>('POST', '/session/face-candidate', body),
  /** An attempt that produced no number. The reason is a code; the reading that failed is never sent. */
  attempt: (outcome: MeasurementAttemptOutcome) =>
    call<CaptureSessionView>('POST', '/session/attempts', { outcome }),
  fail: (reason: CaptureFailureReason) =>
    call<CaptureSessionView>('POST', '/session/fail', { reason }),
};

let connection: Promise<CaptureSessionView> | null = null;
let connectionId: string | null = null;
let arrivedWithCode = false;

/**
 * The one redemption this page load makes.
 *
 * The code leaves the address bar and the history before the request is sent. Development mode
 * runs effects twice and a code works once, so every caller gets the same promise; with no code in
 * the address, the page resumes through the capability cookie instead.
 */
export function connectCapture(): Promise<CaptureSessionView> {
  if (!connection) {
    const token = window.location.hash.slice(1);
    arrivedWithCode = token.length > 0;
    if (window.location.hash) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }
    connectionId = crypto.randomUUID();
    connection = arrivedWithCode
      ? captureClient.redeem(token, coarseDeviceClass())
      : captureClient.session();
  }
  return connection;
}

/**
 * Which session this page is holding, in the only terms a phone has.
 *
 * The phone is never told a session id — it holds a capability and nothing identifying. This stands
 * in for one: it changes whenever the page connects again, which is exactly when the capability
 * changes hands, so a result captured before a rescan cannot be offered after it.
 */
export function captureConnectionKey(): string | null {
  return connectionId;
}

/**
 * A code that reaches a page already open.
 *
 * Scanning again with this page showing changes only the fragment, which reloads nothing, so the
 * new code is taken as a fresh arrival: out of the address bar first, then redeemed.
 */
export function connectWithNewCode(): Promise<CaptureSessionView> | null {
  if (!window.location.hash.slice(1)) return null;
  connection = null;
  return connectCapture();
}

/** Whether this page load arrived with a code, which decides how a refusal is worded. */
export function arrivedWithCaptureCode(): boolean {
  return arrivedWithCode;
}

/** For tests only: a fresh page load. */
export function resetCaptureConnection(): void {
  connection = null;
  connectionId = null;
  arrivedWithCode = false;
}

/** Enough to compare devices in the benchmark; the user-agent string itself never leaves the phone. */
export function coarseDeviceClass(userAgent = navigator.userAgent): CaptureDeviceClass {
  if (/iPhone|iPad|iPod/.test(userAgent) && !/CriOS|FxiOS|EdgiOS/.test(userAgent)) {
    return CaptureDeviceClass.IOS_SAFARI;
  }
  if (/Android/.test(userAgent) && /Chrome\//.test(userAgent))
    return CaptureDeviceClass.ANDROID_CHROME;
  if (/Macintosh/.test(userAgent) && /Safari\//.test(userAgent) && !/Chrome\//.test(userAgent)) {
    return CaptureDeviceClass.DESKTOP_SAFARI;
  }
  if (/Chrome\//.test(userAgent) && !/Mobile/.test(userAgent))
    return CaptureDeviceClass.DESKTOP_CHROMIUM;
  return CaptureDeviceClass.OTHER;
}
